/**
 * Calendar days are not instants.
 *
 * The API sends two different kinds of temporal value through the same JSON:
 *
 *   - INSTANTS — `created_at`, `sent_at`, `dispatched_at`. A real moment, with
 *     an offset. `new Date(x)` is the right SHAPE for these — none of the
 *     local-midnight reconstruction below applies — but it is not always safe
 *     on the raw string: see normalizeInstantString.
 *
 *   - CALENDAR DAYS — `work_date`, `end_date`, `cert_expiry`, `period_start`.
 *     Bare 'YYYY-MM-DD'. `new Date('2026-03-23')` parses this as UTC midnight,
 *     which in Alberta (UTC-6/-7) is 17:00 or 18:00 on the 22nd. Every renderer
 *     downstream then shows the 22nd. A hand picks March 23, the ticket says
 *     March 22, and payroll is off by a day.
 *
 * This module is the chokepoint for the second kind. `no-utc-calendar-date` in
 * eslint.legacy-patterns.js points here; the ~65 sites in
 * scripts/legacy-patterns-baseline.json are the queue to move over.
 *
 * The implementation is the one already proven inline in
 * `src/screens/field/FieldTicketsScreen.tsx` and
 * `src/screens/field/FieldEquipmentDetail.tsx`, lifted so there is one copy.
 */
import { effectiveTimeZone } from './tenant-zone';

/**
 * Parse a calendar day into a Date at LOCAL midnight, so the day the user sees
 * is the day they picked.
 *
 * Accepts 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:mm:ss…' (the time part is discarded —
 * if a value carries a meaningful time it is an instant, and you want
 * `new Date()` instead of this function).
 *
 * Returns null for absent or unparseable input. Callers render the absent case;
 * they do not substitute today.
 */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const iso = String(value).split(/[T ]/)[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  // Rejects '2026-02-31' — JS would silently roll it to March 3.
  return date.getMonth() === Number(mo) - 1 && date.getDate() === Number(d) ? date : null;
}

/**
 * Format a calendar day for display. Returns null when there is no day, so the
 * caller can render an em dash or an explanation rather than "Invalid Date" or,
 * worse, today's date.
 *
 * WHY `locale` IS PINNED AND HAS NO 'BROWSER DEFAULT' SETTING.
 *
 * Six call sites used to hand-roll this so they could finish on a bare
 * `toLocaleDateString()`, and the ratchet baseline carried a reason string
 * defending that as "deliberately the VIEWER's locale". The argument was that
 * routing them here would visibly change the format on a screen the customer
 * reads. It was overturned in PR #507, for two reasons:
 *
 *   - It measured the wrong thing. The comparison it made was against
 *     `toLocaleDateString('en-US')` with NO options, which prints 3/23/2026.
 *     The DEFAULT options below print a month NAME — Mar 23, 2026 — and that
 *     is not what the objection was aimed at.
 *   - The viewer's locale is not a preference here, it is an ambiguity. Two
 *     hands on one crew read the same shift as 2/14/2026 and 14/02/2026, with
 *     nothing on the page saying which. On a field ticket or a cert expiry
 *     that is a real question about which day, not a matter of taste.
 *
 * A month name is unambiguous in every locale, which is the whole point of the
 * default. If a caller genuinely needs a different rendering it passes its own
 * options; what it must not do is hand the decision to whichever phone happens
 * to be holding the screen.
 *
 * The tenant-level `companies.date_format` setting is a SEPARATE question and
 * is deliberately not wired in here. It is a preference, not a safety fix, and
 * honouring it would reformat every currently-unambiguous site in the app.
 */
export function formatCalendarDate(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  locale: string = 'en-US',
): string | null {
  const date = parseCalendarDate(value);
  return date ? date.toLocaleDateString(locale, options) : null;
}

/** The local calendar day as 'YYYY-MM-DD' — the shape the API expects back. */
export function toCalendarDateString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * A stored calendar day as 'YYYY-MM-DD', or null when there is no readable day.
 *
 * Use this where a screen needs the day as a STRING: to prefill a DatePicker or
 * an <input type="date">, to compare with `===`, or to use as a map key.
 *
 * The hand-rolled version was `value.split('T')[0]`, and it is wrong for one of
 * the two spellings the API sends. A Postgres-rendered value
 * ('2026-04-12 00:00:00+00') has a space where ISO has a 'T', so the split
 * returns the whole string. A picker fed that string parsed it as an instant
 * and showed Apr 11 in Alberta, and an `===` against '2026-04-12' never matched
 * (fe#814, fe#815). parseCalendarDate accepts both spellings.
 *
 * Null for absent AND for unreadable input. A caller that must send a value
 * back untouched (a form PUT whole on save) keeps the raw value itself:
 * `calendarDayString(v) ?? v`. That is a decision about that form, so it is
 * made at the call site and not here.
 */
export function calendarDayString(value: string | null | undefined): string | null {
  const day = parseCalendarDate(value);
  return day ? toCalendarDateString(day) : null;
}

/**
 * ─── THE JOB WALL-CLOCK COLUMNS ─────────────────────────────────────────────
 *
 * `jobs.scheduled_date` and `jobs.date_time_wash` are a THIRD kind of temporal
 * value, and neither of the two this module opens with. That is the whole
 * reason `scheduledTimeOfDay` and `formatScheduledTime` exist below.
 *
 * They are `timestamptz`, so they LOOK like instants and the wire spells them
 * '2026-09-10T12:00:00.000Z'. They are not instants. The chain, verified end to
 * end 2026-09-09 rather than assumed:
 *
 *   1. NewJobScreen's `combineDateTime` submits the operator's typed date and
 *      time with NO OFFSET — '2026-09-10T12:00'.
 *   2. Production's session zone is Etc/UTC (`SHOW TimeZone`).
 *   3. `'2026-09-10T12:00:00'::timestamptz` therefore yields
 *      `2026-09-10 12:00:00+00` — the typed wall clock, stamped UTC verbatim.
 *
 * **So 12:00Z MEANS 12:00 to the operator who typed it.** The literal UTC clock
 * IS the tenant's clock for these two columns; read it, do not convert it.
 *
 * WHY formatInstantTime IS THE WRONG TOOL HERE, specifically. It is the obvious
 * reach — the value ends in 'Z' and the helper is ten lines away — and it
 * renders a 12:00 rig-up as `6:00 AM` in Alberta. Six hours early, on an hour
 * plausible enough that no one would question it: 6 AM is a perfectly ordinary
 * time to be on location. The same mistake on the API side was printing 2 AM
 * thread washes on customer call sheets, which is the only reason it was ever
 * noticed.
 *
 * THE OTHER COPY OF THIS REASONING IS IN `fieldiq-api/src/lib/format-date.ts`,
 * in the note above `formatJobWallClockDate` / `jobWallClockTime`. The two
 * repos share no module, so this prose is the only coupling there is. If you
 * change the premise here — because the write path moved, or the cluster's zone
 * moved — that file has to move with it, and nothing will tell you.
 *
 * ─── THE TWO FRONTEND HELPERS DISAGREE ABOUT MIDNIGHT, ON PURPOSE ───────────
 *
 * Same input, opposite correct answers. This is the one thing to read before
 * "simplifying" them into one function:
 *
 *   scheduledTimeOfDay('…T00:00:00.000Z')  ->  '00:00:00'   (PRESERVED)
 *   formatScheduledTime('…T00:00:00.000Z') ->  null         (ABSENT)
 *
 * `00:00:00` is how these columns spell "the office filled the date picker and
 * left the time picker alone" — 25 of 150 scheduled rows estate-wide.
 *
 * A WRITER must carry that midnight across unchanged. The dispatch board's
 * day-drag re-sends this value, and collapsing midnight to null there makes the
 * drag fall back to noon and invent an hour nobody typed — which is the bug
 * that put a hardcoded 12:00 into 18% of the column.
 *
 * A RENDERER must treat the same midnight as absent and show an em dash.
 * Printing "12:00 AM" puts a precise-looking hour on the one row where the crew
 * most needs to know that nothing was set.
 *
 * These are not two formats of one answer, which is why they are not one
 * function with a format argument: such a parameter would carry the ABSENCE
 * RULE as a silent passenger, and the next caller would pick a format and
 * inherit a midnight decision they never considered. Two names, each stating
 * its own answer. `calendar-date.midnight-disagreement.test.ts` asserts the
 * pair against each other so a later unification fails loudly.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The time-of-day half, as 'HH:MM:SS' — the shape these columns are written
 * back in, for a caller re-sending the value. Null only when there is no value
 * at all, so "this job has no date" stays distinguishable from "this job has a
 * date and no time". For display, use `formatScheduledTime`.
 */
export function scheduledTimeOfDay(value: string | null | undefined): string | null {
  if (!value) return null;
  // Bare 'YYYY-MM-DD' carries no time; UTC midnight is the honest reading and
  // is what `new Date()` gives it anyway.
  const dt = new Date(normalizeInstantString(String(value)));
  if (Number.isNaN(dt.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(dt.getUTCHours())}:${pad(dt.getUTCMinutes())}:${pad(dt.getUTCSeconds())}`;
}

/**
 * A job's scheduled time of day for DISPLAY — '12:00 PM'. Null when the job has
 * no time, so the caller renders an em dash.
 *
 * Built ON `scheduledTimeOfDay` rather than beside it: the UTC extraction and
 * the pg/JavaScriptCore string-shape handling live in exactly one place, and
 * this adds only the two things a renderer needs — the midnight-is-absent rule
 * and the 12-hour formatting. See the note above that function for why the
 * literal UTC clock is the right read, why `formatInstantTime` is not, and why
 * these two deliberately answer differently on midnight.
 *
 * Returns null in three cases the caller renders identically as '—': no value,
 * an unparseable value, and exact midnight. That is intentional collapsing —
 * to a hand reading the screen, "no date", "a broken date" and "a date with no
 * time" are all "nobody has told me when", and the honest render for all three
 * is the dash rather than a confident-looking hour.
 *
 * Locale is pinned for the same reason `formatCalendarDate` pins it: en-US
 * gives an unambiguous 12-hour clock with AM/PM, where a viewer's locale could
 * silently produce a 24-hour one and make 06:00 and 18:00 harder to tell apart
 * at a glance on a phone in the dark.
 */
export function formatScheduledTime(value: string | null | undefined): string | null {
  const hms = scheduledTimeOfDay(value);
  if (!hms || hms === '00:00:00') return null;
  const [h, m] = hms.split(':').map(Number);
  // Constructed in LOCAL time purely as a formatting vehicle — the date part is
  // never read, and the clock values are the literal UTC ones from above, so no
  // zone conversion can happen here.
  return new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Today, as the TENANT's calendar sees it — 'YYYY-MM-DD' in
 * `companies.time_zone` — or null when the zone is not known.
 *
 * This is the mirror image of the parse bug and the one to reach for when
 * pre-filling a <input type="date">. `new Date().toISOString().split('T')[0]`
 * is UTC's date, so in Alberta every entry made after 18:00 local pre-fills
 * TOMORROW. Evening shift is normal here, so that is not an edge case.
 *
 * It used to answer in the DEVICE's zone, which is the same mistake one step
 * removed: a dispatcher in Edmonton planning a Chicago tenant's day got
 * Edmonton's today, and a laptop left on UTC got Greenwich's. The zone is now a
 * required argument — pass `useTenantTimeZone()` — so there is no way to call
 * this without saying whose today it is.
 */
export function todayCalendarDate(timeZone: string | null | undefined, now: Date = new Date()): string | null {
  return tenantCalendarDate(timeZone, now);
}

/** Whole days from calendar day `a` to calendar day `b` (both 'YYYY-MM-DD'). */
function calendarDaySpan(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  // UTC on both sides: there is no zone in a calendar day, so no DST hour can
  // make the span 23 or 25 hours.
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/**
 * Whole calendar days from the TENANT's today until `value`. Negative when it
 * has passed, 0 when it is today.
 *
 * Both sides are calendar days, so a cert expiring today reads as 0 days left
 * rather than "expired 1 day ago" purely because the clock has moved past
 * midnight somewhere. "Today" is the tenant's (`companies.time_zone`), not the
 * device's. Returns null for an absent or unparseable day, or an unknown zone —
 * callers render that case, they do not treat it as 0.
 */
export function daysUntilCalendarDate(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): number | null {
  const target = calendarDayString(value ?? null);
  const today = tenantCalendarDate(timeZone, now);
  if (!target || !today) return null;
  return calendarDaySpan(today, target);
}

/**
 * ── READING AN INSTANT THE API SENT ─────────────────────────────────────────
 *
 * Rewrite a timestamp into the one shape every JS engine parses strictly.
 *
 * The API sends `timestamptz` two different ways depending on the path it took.
 * Where node-pg hands back a Date and the serialiser calls `toISOString()`, it
 * arrives as `2026-08-02T13:20:08.577Z` and every engine agrees. Where it does
 * not, it arrives as Postgres' own rendering:
 *
 *   2026-08-02 13:20:08.577258+00
 *        ^ space, not 'T'          ^ bare hour offset, no ':00'
 *
 * V8 parses that leniently, so it looks fine in Chrome. Older JavaScriptCore
 * does not, and this app ships to iOS through Capacitor — there it would come
 * back Invalid Date and the value would silently downgrade to "not recorded" on
 * exactly the devices the field crew uses.
 *
 * ── BOTH REWRITES, AND WHY HALF IS WORSE THAN NONE ──────────────────────────
 *
 * `2026-08-02 13:20:08+00` parses correctly in BOTH V8 and JavaScriptCore,
 * because the space makes it a non-ISO string and each engine falls back to a
 * lenient parser that copes with the short zone. Insert the `T` ALONE and it
 * becomes a strict-ISO candidate carrying an illegal zone, and BOTH engines
 * return NaN. Measured on node 20 and on JavaScriptCore via `osascript -l
 * JavaScript` (Safari 26.5); they agreed on all ten forms tried. So the zone
 * expansion is not defensive padding — it is what stops the separator rewrite
 * from breaking a string that already worked.
 *
 * ── THE TWO GUARDS ──────────────────────────────────────────────────────────
 *
 * Each of these was missing from one of the two copies this helper replaces,
 * and each is load-bearing:
 *
 *   1. The separator rewrite is ANCHORED ON A DATE PREFIX, so only the
 *      date/time separator is touched. A blind `.replace(' ', 'T')` rewrites
 *      the first space anywhere in the string.
 *   2. The zone rewrite is SKIPPED WHEN THERE IS NO TIME COMPONENT. A
 *      date-only `2026-09-06` ends in `-06`, and an ungated rewrite turns it
 *      into `2026-09-06:00` — a calendar day silently reinterpreted as a
 *      timezone, and then unparseable.
 *
 * A value carrying no zone at all is left alone rather than having `Z`
 * appended: appending one invents a timezone and moves the instant by the
 * device's offset, where leaving it means the engine applies its documented
 * rule for that shape.
 *
 * This is for INSTANTS. A calendar day belongs in parseCalendarDate, which
 * reconstructs it at local midnight — passing one here and calling `new Date`
 * on the result is the UTC-midnight bug this module exists to prevent.
 */
export function normalizeInstantString(value: string): string {
  const withT = value.trim().replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T');
  return /T\d{2}:\d{2}/.test(withT) ? withT.replace(/([+-]\d{2})$/, '$1:00') : withT;
}

/**
 * Milliseconds since the epoch for an instant, or null.
 *
 * Returns null rather than NaN so a caller cannot arithmetic its way to a
 * plausible-looking number: NaN propagates through `-` and `/` and only
 * surfaces at the render, by which point it reads as a formatting bug rather
 * than a parse failure.
 */
export function parseInstantMs(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  const ms = Date.parse(normalizeInstantString(value));
  return Number.isFinite(ms) ? ms : null;
}

/** The same instant as a Date, or null. For callers that want the object. */
export function parseInstantDate(value: unknown): Date | null {
  const ms = parseInstantMs(value);
  return ms === null ? null : new Date(ms);
}

/**
 * The DAY an instant falls on, in the viewer's own zone.
 *
 * The counterpart to formatCalendarDate, and the reason this file now has both:
 * three screens had each hand-rolled this, every one of them carrying a comment
 * arguing that formatCalendarDate is the WRONG target here. They were right.
 * `scripts/legacy-patterns-baseline.json` recorded the same conclusion and left
 * the decision open — "this repo has no shared instant formatter; adding one is
 * its own change, and this count is what says whether it is worth making." The
 * count reached three, and this is that change.
 *
 * The distinction is not pedantry. An invoice created at 21:30 local on Jul 14
 * is stored '2026-07-15T03:30:00Z'. formatCalendarDate reads the UTC prefix and
 * answers Jul 15; only converting to local time answers Jul 14. Reaching for
 * the calendar-day helper because a value "looks like a date" is the same
 * mistake as no-utc-calendar-date, pointed the other way — and it printed a
 * wrong creation date on the customer's copy of the invoice.
 *
 * Returns null for absent or unparseable input, so callers render the absent
 * case rather than 'Invalid Date'. *
 * IT PARSES THE POSTGRES SHAPE, and that is not cosmetic. A `timestamptz`
 * selected raw arrives as '2026-09-04 00:56:18.511471+00' — a space instead of
 * the T, and a two-digit offset. V8 accepts it leniently and **JavaScriptCore
 * rejects it**, so a bare `new Date()` here reads correctly on every desktop
 * and silently degrades to the absent branch on the iOS build only. That is
 * already on file in CLAUDE.md as one of the silent-blank-field family, and
 * this file's own `parseInstantMs` has normalised the shape since it was
 * written — these three renderers were the ones that did not route through it.
 * Fixed at the chokepoint rather than at the ~20 call sites, per "the correct
 * helper usually already exists": an instance fix here guarantees instance N+1.
 */
export function formatInstantDay(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  locale: string = 'en-US',
): string | null {
  return formatInZone(value, timeZone, options, locale, 'date');
}

/**
 * The one place an instant is turned into words, and always in a NAMED zone.
 * Null for an absent value or an unusable zone — never the device's zone as a
 * stand-in, which is what a missing `timeZone` option means to Intl.
 */
function formatInZone(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale: string,
  part: 'date' | 'time' | 'both',
): string | null {
  const at = parseInstantDate(value);
  if (!at || !timeZone) return null;
  const opts = { ...options, timeZone: effectiveTimeZone(timeZone, at) };
  try {
    if (part === 'date') return at.toLocaleDateString(locale, opts);
    if (part === 'time') return at.toLocaleTimeString(locale, opts);
    return at.toLocaleString(locale, opts);
  } catch {
    return null; // an unrecognised zone throws RangeError
  }
}

/**
 * The clock-time half of an instant — "2:47 PM" — for timestamps that ARE
 * instants (clock_in_at, clock_out_at). Same shape and reason as
 * formatInstantDay: null for absent or unparseable input, so a caller renders
 * the absent case rather than 'Invalid Date'. Added for the clock-in screen's
 * own-shift list; ApprovalQueueScreen and TimesheetDetailModal carry older
 * local copies of this that predate it.
 */
export function formatInstantTime(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true },
  locale: string = 'en-US',
): string | null {
  return formatInZone(value, timeZone, options, locale, 'time');
}

/**
 * Day AND clock time of an instant — "Sep 5, 2:47 PM" — for a trail entry or
 * a from/to value where the day alone would hide a shift that crossed
 * midnight. Null on absent or unparseable input, like its two siblings.
 */
export function formatInstant(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
  locale: string = 'en-US',
): string | null {
  return formatInZone(value, timeZone, options, locale, 'both');
}

/**
 * The calendar day in a NAMED ZONE — 'YYYY-MM-DD' — or null if the zone is
 * unusable.
 *
 * The third member of this file's family, and the one the other two cannot
 * stand in for. `formatCalendarDate` renders a day that is already a day;
 * `todayCalendarDate` answers what day it is HERE. This answers what day it is
 * THERE, which is a different question and has a different wrong answer.
 *
 * It exists because a tenant-stamped day has to be compared against that
 * tenant's today, not the viewer's. `morning_briefs.brief_date` is written in
 * the company's own zone; comparing it to `todayCalendarDate()` marks a brief
 * as stale for anyone sitting east of the tenant after midnight their time —
 * a wrong answer produced by the right-looking helper, exactly the shape
 * formatCalendarDate's own header warns about.
 *
 * en-CA is pinned because it renders YYYY-MM-DD, which is the shape the API
 * stores and the only one that compares correctly with `<`.
 *
 * Returns null rather than falling back to the viewer's zone: an unusable zone
 * is not evidence about what day it is anywhere, and a caller has to be able to
 * say nothing instead of guessing.
 */
export function tenantCalendarDate(
  timeZone: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!timeZone) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: effectiveTimeZone(timeZone, now),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    // An unrecognised IANA zone throws RangeError. Nothing is known, so nothing
    // is claimed.
    return null;
  }
}

/**
 * The TENANT's calendar day an instant fell on — 'YYYY-MM-DD' — or null.
 *
 * `tenantCalendarDate(timeZone, parseInstantDate(x))`, in the one place it is
 * spelled. For the instants a screen shows as a day: invoices.invoice_date /
 * due_date, invoice_payments.payment_date, quotes.quote_date,
 * equipment.last/next_inspection_date (api#838). An invoice written at 19:30
 * MDT on Sep 14 is '2026-09-15T01:30:00Z'; its UTC prefix says Sep 15, and
 * so did every screen that read it through formatCalendarDate.
 *
 * Two things the bare composition gets wrong, and the reason it lives here:
 *   - `parseInstantDate` returns null for absent input, and
 *     `tenantCalendarDate(tz, null)` formats epoch 0 — Dec 31, 1969 — because
 *     Intl reads null as 0. The absent case has to stop before the zone.
 *   - A bare 'YYYY-MM-DD' is a calendar day already. The API rewrites only a
 *     zoned timestamp under these keys, and `due_date` is a `date` column on
 *     the trucking, rental and downhole invoices, so a day can arrive here.
 *     Parsed as an instant it is UTC midnight — the previous day in Alberta.
 *
 * Null for absent, unparseable, or an unusable zone; render the em dash.
 */
export function instantTenantDay(value: unknown, timeZone: string | null | undefined): string | null {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return calendarDayString(value.trim());
  }
  const at = parseInstantDate(value);
  return at ? tenantCalendarDate(timeZone, at) : null;
}

/** `instantTenantDay`, formatted as formatCalendarDate formats a day. */
export function formatInstantTenantDay(
  value: unknown,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  locale: string = 'en-US',
): string | null {
  return formatCalendarDate(instantTenantDay(value, timeZone), options, locale);
}

/**
 * A relative label for a calendar day, at DAY precision — 'today',
 * 'yesterday', '5 days ago', 'tomorrow', 'in 5 days'. Null when there is no
 * usable day.
 *
 * This exists because the alternative keeps being "hand it to the screen's
 * formatTimeAgo", and that helper renders INSTANTS. Given a calendar day it
 * parses UTC midnight and then reports an HOURS figure about a value that has
 * no time of day at all — which is how a meeting held this morning read as
 * "16 hours ago", and a cert expiring today read "has expired".
 *
 * Precision is the point, not just correctness: a calendar day cannot support
 * "3 hours ago" and must not pretend to. Callers that genuinely have an instant
 * should keep using their own relative-time helper; this is for the arm where
 * the instant is absent and only the day survives.
 */
export function relativeCalendarDay(
  value: string | null | undefined,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const days = daysUntilCalendarDate(value, timeZone, now);
  if (days === null) return null;
  if (days === 0) return 'today';
  if (days === -1) return 'yesterday';
  if (days === 1) return 'tomorrow';
  return days < 0 ? `${-days} days ago` : `in ${days} days`;
}

/**
 * Relative age of an INSTANT — "5 min ago", "2 hours ago", "yesterday".
 *
 * The instant-shaped sibling of `relativeCalendarDay`, which does the same job
 * for a calendar day and must NOT be used for one of these (or the other way
 * round): a bare `YYYY-MM-DD` parses to UTC midnight, so this would report an
 * hours-precision age for a value with no time of day at all, and read "16
 * hours ago" for something that happened today.
 *
 * Lived twice, byte-identical, one copy per safety screen. Moved here rather
 * than into the feed module so it sits beside the calendar-day version a
 * caller has to choose between.
 */
export function formatTimeAgo(dateStr: string | undefined, timeZone: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  // Anything older than a week falls back to a date, and that fallback used to
  // be the reader's own device locale — inside the module that exists to stop
  // exactly that. Same pinned month name as formatInstantDay, which is what
  // this arm is: an instant, rendered as the day it fell on.
  //
  // And in the tenant's zone, not the device's: an instant shown as a day is
  // the tenant's day. No zone, no date — an em dash rather than a guess.
  return formatInstantDay(dateStr, timeZone) ?? '—';
}

/**
 * ── THE TENANT'S CLOCK ─────────────────────────────────────────────────────
 *
 * The wall-clock fields of an instant as the TENANT reads them, or null for an
 * unusable zone. The primitive under every "what day / hour is it there"
 * question below; nothing here ever asks the device.
 */
export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  weekday: number; // 0 = Sunday, matching Date#getDay
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function zonedParts(at: Date, timeZone: string | null | undefined): ZonedParts | null {
  if (!timeZone || !Number.isFinite(at.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: effectiveTimeZone(timeZone, at),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hourCycle: 'h23',
    }).formatToParts(at);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return {
      year: Number(get('year')),
      month: Number(get('month')),
      day: Number(get('day')),
      hour: Number(get('hour')) % 24,
      minute: Number(get('minute')),
      weekday: WEEKDAYS.indexOf(get('weekday')),
    };
  } catch {
    return null;
  }
}

/**
 * The tenant's today as a LOCAL-MIDNIGHT Date — for calendar arithmetic
 * (month grids, week starts, date-fns ranges) that wants a Date object.
 *
 * Built from the tenant's calendar day, then constructed locally like every
 * other calendar day in this file, so `getDate()` / `getDay()` on it answer
 * for the TENANT's day whatever the device's zone. Use it in place of a bare
 * `new Date()` wherever "today" anchors a calendar. Null for an unknown zone.
 */
export function tenantTodayDate(timeZone: string | null | undefined, now: Date = new Date()): Date | null {
  return parseCalendarDate(todayCalendarDate(timeZone, now));
}

/** The tenant's current hour, 0-23 — a greeting, a shift cut-off. Null for an unknown zone. */
export function tenantHour(timeZone: string | null | undefined, now: Date = new Date()): number | null {
  return zonedParts(now, timeZone)?.hour ?? null;
}

/**
 * The instant at which the TENANT's wall clock reads y-m-d h:mi, or null.
 *
 * The write half: a time typed into a picker is a time on the tenant's clock,
 * not the device's. An office in Edmonton correcting a Chicago crew's clock-out
 * to "17:00" means 17:00 in Chicago.
 *
 * Two passes of offset correction, which settles every real zone; in the hour
 * a DST change skips, the earlier offset is kept (the conventional reading),
 * and in the hour it repeats, the first occurrence.
 */
export function zonedWallClockToInstant(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  timeZone: string | null | undefined,
): Date | null {
  if (!timeZone) return null;
  const wanted = Date.UTC(y, mo - 1, d, h, mi);
  let guess = wanted;
  for (let i = 0; i < 2; i += 1) {
    const p = zonedParts(new Date(guess), timeZone);
    if (!p) return null;
    const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += wanted - seen;
  }
  const out = new Date(guess);
  return Number.isFinite(out.getTime()) ? out : null;
}
